import { inject, Injectable } from '@angular/core';
import moment from 'moment';
import { map } from 'rxjs';

import { KeyValueModel } from '@models/common';
import { FieldTypeEnum } from '@models/module/fields/enums';
import { DataListFieldPropertiesModel, DataListType } from '@models/module/fields/main';
import {
  FilterLogic,
  FilterModel,
  FilterOperator,
  GridSearchEntityInstance,
  GridViewModel,
  SearchViewModel,
} from '@models/module/grid';
import { ScreenModel, ScreenTypeEnum } from '@module-definition/screen/common';
import { ModuleGridService } from '@module-instance/services';
import { TableDataService } from '@shared/components/data-list/common';
import { HttpService } from '@shared/services';

@Injectable({ providedIn: 'root' })
export class CalendarService {
  private httpService = inject(HttpService);
  private moduleGridService = inject(ModuleGridService);
  private tableDataService = inject(TableDataService);

  getData(contractId: number, field: string, date1: Date, date2: Date, search?: SearchViewModel) {
    const body: SearchViewModel = {
      page: 1,
      pagesize: 100,
      isCurrent: search?.isCurrent ?? true,
      criteria: search?.criteria,
      filter: { ...this.makeFilter(search?.filter, field, date1, date2) },
    };
    return this.moduleGridService.gridSearch({ body, contractId, dataListType: DataListType.Calendar }).pipe(
      map(result => ({
        ...result,
        data: this.tableDataService.mapTableRowsState(result?.data) as unknown as GridSearchEntityInstance[],
      })),
    );
  }

  getDataListFields() {
    return this.httpService.post<GridViewModel<DataListFieldPropertiesModel[]>>(
      `_api/DataList/Search?dataListType=${DataListType.Calendar}`,
      {},
      { useLoader: false },
    );
  }

  getDescriptionReferenceFields() {
    return this.httpService
      .get<KeyValueModel<number, string>[]>(`_api/FieldDefinition/EntityFieldsDict`, { useLoader: false })
      .pipe(map(d => d.map(e => ({ id: e.key, name: e.value, label: e.description }))));
  }

  getDateReferenceFields() {
    const params = this.httpService.buildHttpParams({
      type: FieldTypeEnum.Date,
      addContractFields: false,
      includeSystemFields: true,
      includeSpecialFields: true,
    });
    return this.httpService
      .get<KeyValueModel<number, string>[]>(`_api/FieldDefinition/Dict`, { useLoader: false, httpOptions: { params } })
      .pipe(map(d => d.map(e => ({ id: e.key, name: e.description, label: e.value }))));
  }

  getScreen() {
    return this.httpService
      .post<
        GridViewModel<ScreenModel[]>
      >(`_api/Screen/Search?type=${ScreenTypeEnum.Calendar}`, {}, { useLoader: false })
      .pipe(map(s => (s?.data && s.data.length ? s.data[0] : null)));
  }

  private makeFilter(filter: FilterModel, field: string, date1: Date, date2: Date): FilterModel {
    if (!filter) {
      return this.pushFilter(this.pushFilter(this.makeFilterBetweenDates(field, date1, date2)));
    }

    let found = false;
    filter = { ...filter };
    filter.filters = filter.filters || [];
    if (filter.filters.length) {
      for (const f of filter.filters) {
        if (f.filters && Array.isArray(f.filters)) {
          // try to find a filter for this field
          let sf = f.filters.find(s => s.field && s.field == field);
          if (sf) {
            // replace the filter
            sf = this.makeFilterBetweenDates(field, date1, date2);
            found = true;
          }
        }
      }
    }
    if (!found) {
      // no filter found on this field, add the filter
      filter.filters.push(this.pushFilter(this.makeFilterBetweenDates(field, date1, date2)));
    }
    return filter;
  }

  private makeFilterBetweenDates(field: string, date1: Date, date2: Date): FilterModel {
    return {
      field: field,
      fieldTypeId: FieldTypeEnum.Date,
      operator: FilterOperator.Between,
      value: [moment(date1).format('YYYY-MM-DD'), moment(date2).format('YYYY-MM-DD')],
    } as FilterModel;
  }

  private pushFilter(filter: FilterModel): FilterModel {
    return {
      logic: FilterLogic.AND,
      filters: [filter],
    } as FilterModel;
  }
}
